// Private ID / applicant photos (Cloudinary "authenticated" assets).  // sec: private-id-photos
// The admin UI never receives a permanent URL: it asks the backend for a short-lived
// (~5 min) download link on demand. Every request is audited server-side.
import React, { useEffect, useState } from "react";
import { api } from "./api";

const ROUTES = {
  collab: (id, slot) => `/api/collab-submissions/${encodeURIComponent(id)}/id-photo-url/${slot}`,
  modelapply: (id, slot) => `/api/model-applications/${encodeURIComponent(id)}/photo-url/${slot}`,
  legal: (id) => `/api/legal/${encodeURIComponent(id)}/file-url`,
};
const CONTENT_ROUTES = {
  collab: (id, slot) => `/api/collab-submissions/${encodeURIComponent(id)}/id-photo-content/${slot}`,
  modelapply: (id, slot) => `/api/model-applications/${encodeURIComponent(id)}/photo-content/${slot}`,
};

// Field names per record kind. collab slots: id_front / id_back / selfie.
export function photoAsset(record, kind, slot) {
  return record?.[`${slot}_asset`];
}
export function legacyPhotoUrl(record, kind, slot) {
  return kind === "collab" ? record?.[`${slot}_url`] : record?.[slot];
}
export function hasPhoto(record, kind, slot) {
  return !!(photoAsset(record, kind, slot)?.public_id || legacyPhotoUrl(record, kind, slot));
}

// → { url, expires_at, legacy_public }
export async function fetchPhotoLink(kind, id, slot) {
  const r = await api.get(ROUTES[kind](id, slot));
  return r.data;
}

// Bytes for one photo as a data: URL (PDF export). Private assets are proxied by the backend
// (admin + audited); legacy unmigrated photos are fetched from their old public URL.
export async function fetchPhotoDataUrl(kind, id, slot, record) {
  let blob;
  if (photoAsset(record, kind, slot)?.public_id && CONTENT_ROUTES[kind]) {
    const r = await api.get(CONTENT_ROUTES[kind](id, slot), { responseType: "blob" });
    blob = r.data;
  } else {
    const legacy = legacyPhotoUrl(record, kind, slot);
    if (!legacy) return null;
    if (legacy.startsWith("data:")) return legacy;
    const res = await fetch(legacy, { mode: "cors" });
    if (!res.ok) return null;
    blob = await res.blob();
  }
  return await new Promise((resolve) => {
    const fr = new FileReader();
    fr.onload = () => resolve(fr.result);
    fr.onerror = () => resolve(null);
    fr.readAsDataURL(blob);
  });
}

// Open a fresh short-lived link in a new tab (tab opened synchronously to dodge popup blockers).
export async function openPrivateFile(kind, id, slot) {
  const w = window.open("", "_blank");
  try {
    const { url } = await fetchPhotoLink(kind, id, slot);
    if (w) { w.opener = null; w.location.href = url; } else { window.location.assign(url); }
  } catch (e) {
    if (w) w.close();
    throw e;
  }
}

// <img> that loads a short-lived link when it mounts; click opens a fresh link full size.
export function PrivatePhoto({ kind, id, slot, alt, className, testid }) {
  const [state, setState] = useState({ url: "", error: "" });
  useEffect(() => {
    let alive = true;
    setState({ url: "", error: "" });
    fetchPhotoLink(kind, id, slot)
      .then((d) => alive && setState({ url: d.url, error: "" }))
      .catch(() => alive && setState({ url: "", error: "Could not load" }));
    return () => { alive = false; };
  }, [kind, id, slot]);
  const open = (e) => {
    e.preventDefault();
    openPrivateFile(kind, id, slot).catch(() => {});
  };
  if (!state.url) {
    return React.createElement("div", {
      className: `${className || ""} flex items-center justify-center text-[10px] text-zinc-600`,
      "data-testid": testid ? `${testid}-loading` : undefined,
    }, state.error || "Loading…");
  }
  return React.createElement("a", { href: "#", onClick: open, title: "Open (link expires in ~5 min)" },
    React.createElement("img", { src: state.url, alt, className, "data-testid": testid,
      referrerPolicy: "no-referrer", onError: () => setState({ url: "", error: "Link expired — reopen" }) }));
}
